/**
 * `fetchBoard` — pulls the full board for the active guild and maps the
 * GraphQL shapes into the frontend domain types. Lives outside the slim
 * composer because the body is large (numeric-id map building +
 * player-by-group / group-by-room / player-by-room reconstruction) and
 * the slim composer is meant to compose sub-modules, not contain them.
 *
 * Concurrency contract:
 *   • Coalesces multiple callers into a single in-flight request
 *     (`inFlightFetch`).
 *   • Records a generation counter; a refetch started while a newer
 *     one is in flight is dropped when its response lands (the newer
 *     call already owns the state).
 *   • Sets `loading.value = false` on first completion so the UI's
 *     initial-load spinner clears.
 */
import type { Ref } from 'vue'

import { resolveClient } from '@/shared/ports/resolveClient'
import { applyBoardSnapshot } from './applySnapshot'
import type { BoardPayload } from './mappers'
import type { Player, PlayerGroup, Room } from '../../types'
import type { BuildPreset, PlayerBuildPresetOwner } from '../../types'
import { BuildPresetLibraryDocument } from '../buildPresets/graphql'
import { mapBuildPresetLibrary } from '../buildPresets/mappers'
import type { BuildPresetLibraryQuery } from '../buildPresets/mappers'

// Local alias — see `stores/board/actions.ts` for the rationale.
const apolloClient = resolveClient()

export interface FetchBoardRefs {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly error: Ref<string | null>
  readonly loading: Ref<boolean>
  readonly players: Ref<ReadonlyArray<Player>>
  readonly guildPlayerDiscordIds?: Ref<ReadonlyArray<string> | null>
  readonly groups: Ref<ReadonlyArray<PlayerGroup>>
  readonly rooms: Ref<ReadonlyArray<Room>>
  readonly fetchGeneration: Ref<number>
  readonly numericRoomId: Map<string, number>
  readonly numericGroupId: Map<string, number>
  readonly playerBuildPresetOwners: Ref<ReadonlyArray<PlayerBuildPresetOwner>>
  readonly guildBuildPresets: Ref<ReadonlyArray<BuildPreset>>
  readonly clearBuildPresetLibrary: () => void
  /** Side-effects to run after the response is committed. */
  readonly resolveDbGuildId: () => Promise<number | null>
  readonly fetchPlayerTags: () => Promise<void>
}

export interface FetchBoardApi {
  /**
   * Trigger a board fetch. Ordinary callers coalesce into an in-flight
   * request. A mutation caller passes `true` to wait for an older read and
   * then issue a new authoritative request, so a just-written change cannot
   * be replaced by the pre-mutation response.
   */
  readonly fetchBoard: (afterInFlight?: boolean) => Promise<void>
  /** Discard a pending HTTP response after newer subscription state arrives. */
  readonly invalidateFetch: () => void
}

export function useFetchBoard(refs: FetchBoardRefs): FetchBoardApi {
  let inFlightFetch: Promise<void> | null = null
  let inFlightGuildId: string | null = null
  let requestedGuildId: string | null = null

  async function fetchBoard(afterInFlight = false): Promise<void> {
    const snowflake = refs.activeDiscordGuildId.value
    if (snowflake === '0') {
      requestedGuildId = snowflake
      refs.players.value = []
      if (refs.guildPlayerDiscordIds) refs.guildPlayerDiscordIds.value = null
      refs.groups.value = []
      refs.rooms.value = []
      refs.numericRoomId.clear()
      refs.numericGroupId.clear()
      refs.clearBuildPresetLibrary()
      refs.loading.value = false
      return
    }
    if (requestedGuildId !== snowflake) {
      requestedGuildId = snowflake
      refs.players.value = []
      if (refs.guildPlayerDiscordIds) refs.guildPlayerDiscordIds.value = null
      refs.groups.value = []
      refs.rooms.value = []
      refs.numericRoomId.clear()
      refs.numericGroupId.clear()
      refs.clearBuildPresetLibrary()
      refs.error.value = null
      refs.loading.value = true
    }
    if (inFlightFetch !== null) {
      if (inFlightGuildId !== snowflake) {
        // A response for the previously selected guild must never hydrate the
        // new guild's board. Let it finish in the background, invalidate its
        // generation, and issue a separate request for the active guild.
        refs.fetchGeneration.value += 1
        inFlightFetch = null
        inFlightGuildId = null
      } else if (!afterInFlight) {
        return inFlightFetch
      } else {
        await inFlightFetch
        return fetchBoard()
      }
    }
    const generation = refs.fetchGeneration.value + 1
    refs.fetchGeneration.value = generation
    let request: Promise<void> | null = null
    const run = async (): Promise<void> => {
      try {
        const { BoardDocument } = await import('../../lib/handWrittenGraphql')
        const res = await apolloClient.query({
          query: BoardDocument,
          variables: { guildId: snowflake },
          fetchPolicy: 'network-only',
        })
        if (
          generation !== refs.fetchGeneration.value
          || snowflake !== refs.activeDiscordGuildId.value
        ) return
        const data = res.data as BoardPayload
        const boardPayload = data.board
        if (!boardPayload) {
          refs.loading.value = false
          refs.error.value = `Guild ${snowflake} is not registered yet — ask the bot owner to run /register_guild in Discord.`
          return
        }
        applyBoardSnapshot(refs, boardPayload)
        if (refs.guildPlayerDiscordIds) {
          refs.guildPlayerDiscordIds.value = data.guildPlayerDiscordIds ?? null
        }

        const presetResult = await apolloClient.query({
          query: BuildPresetLibraryDocument,
          variables: { guildId: snowflake },
          fetchPolicy: 'network-only',
        })
        if (
          generation !== refs.fetchGeneration.value
          || snowflake !== refs.activeDiscordGuildId.value
        ) return
        const presetData = presetResult.data as BuildPresetLibraryQuery | null
        if (presetData?.buildPresetLibrary === undefined) {
          throw new Error('Build preset library returned no snapshot.')
        }
        const library = mapBuildPresetLibrary(presetData.buildPresetLibrary)
        refs.playerBuildPresetOwners.value = library.playerPresets
        refs.guildBuildPresets.value = library.guildPresets

        void refs.resolveDbGuildId()
        void refs.fetchPlayerTags()

        refs.loading.value = false
        refs.error.value = null
      } catch (e: unknown) {
        if (
          generation !== refs.fetchGeneration.value
          || snowflake !== refs.activeDiscordGuildId.value
        ) return
        refs.loading.value = false
        refs.error.value = e instanceof Error ? e.message : String(e)
      } finally {
        if (inFlightFetch === request) {
          inFlightFetch = null
          inFlightGuildId = null
        }
      }
    }
    request = run()
    inFlightFetch = request
    inFlightGuildId = snowflake
    return request
  }

  function invalidateFetch(): void {
    refs.fetchGeneration.value += 1
  }

  return { fetchBoard, invalidateFetch }
}
